import { CodeTitle } from '@pmp/hooks/projectFilters';
import { UserProfileExtended } from '@pmp/projectPortalTypes';
import type { EXPERIENCE_CATEGORIES, GENDER_OPTIONS, SKILL_CHOICES } from '@shared/constants';
import type { Bounds, IdTitle } from '@shared/types';
import { CancelToken } from 'axios';
import type {
  CAREER_INTEREST_TESTS,
  CAREER_INTEREST_TESTS_STEPS,
} from './components/CareerInterestsTest/constants';
import { OnJobTrainingVacancyTypes } from './components/Jobs/types';

export type ExperienceCategory = (typeof EXPERIENCE_CATEGORIES)[number];

export type SkillGroups =
  | 'communication_collaboration_and_creativity'
  | 'information_skills'
  | 'assisting_and_caring'
  | 'management_skills'
  | 'working_with_computers'
  | 'handling_and_moving'
  | 'constructing'
  | 'working_with_machinery_and_specialised_equipment'
  | 'knowledge'
  | 'soft_skills'
  | 'practical_skills';

export const windowExtended = window as typeof window & {
  project: {
    slug?: string;
    title?: string;
    access_code?: string;
    profile_access: string;
    features: string[];
    logo?: string;
    allowed_locales?: string[];
    allowed_languages?: string[];
    project_type?: 'organization' | 'full' | 'lite' | 'skill_survey';
  };
  projectLogo: string;
  invitation?: {
    locale: string;
    token: string;
    email: string;
  };
  themeColors?: ThemeColors;
};
export type Keyword<TId = string> = { id: TId; text: string };
export type ExperienceKeyword = Keyword<string> & { category?: string };
export type EducationStatus = 'finished' | 'not_finished' | 'unknown';
export type Experience = Readonly<{
  id: number;
  category: ExperienceCategory;
  startDate: string;
  endDate: string | null;
  city: string;
  country: string;
  self_employed: boolean;
  employer: string;
  title: string;
  duration_per_week: string;
  education_status: null | EducationStatus;
  education_type: string;
  education_type_other: string;
  education_type_ranking: number | null;
  education_type_label: string | null;
  institution: string;
  keywords: ExperienceKeyword[];
  occupations: Array<Keyword<number> & { external_id: string | null; title: string }>;
  course_ids?: number[];
  ongoing: boolean;
  completed: boolean;
  recommended_skill_points: number | null;
  include_on_cv: boolean;
  user_picked_skills: boolean;
  description?: string;
  redirected_from_course?: number;
  expirable: boolean;
  reviewed?: boolean;
  code?: string;
}>;

export type Pagination = {
  currentPage: number;
  perPage: number;
  totalEntries: number;
};

export type ExperienceInput = Partial<
  Omit<Experience, 'occupations' | 'progress'> & {
    occupation_ids?: number[];
    aimed_occupation_ids?: number[];
  }
>;

export type Skill = Readonly<{
  id: number;
  title: string;
  description?: string;
  skill_type: string;
}>;

export type CareerGoal = {
  id: number;
  occupationId: number;
  externalId: string;
  title: string;
  description?: string;
  tags?: string[];
  coursesCount?: number;
  promoted?: boolean;
  customCategoryId: number;
};

export type CustomCategoryGoal = {
  id: number;
  custom_category_id: number;
};

export type Image = {
  url: string;
  width: number;
  height: number;
  gravity: string;
  alt: string;
};

type SkillBase = {
  id: number;
  skillId: number;
  title: string;
};

type CareerBase = {
  id: number;
  customCategoryId: number;
  title: string;
};

type CourseLocation = {
  name: string;
  country: string;
  city: string;
};

export type Course = {
  id: number;
  title: string;
  description: string;
  url: string;
  matchingSkillCount?: number;
  skillCount: number;
  relevantCareerCount?: number;
  matchingSkills?: SkillBase[];
  skills: SkillBase[];
  relevantCareerFields?: CareerBase[];
  relevantCareers?: CareerBase[];
  inDemand?: boolean;
  matchingCareer?: IdTitle[];
  matchingCareerCount?: number;
  image?: string | null;
  provider: {
    name: string;
    logo: string | null;
  };
  location?: null | CourseLocation;
  courseType?: 'offline' | 'online' | '' | null;
};

export type ImageType = {
  url: string;
  width: number;
  height: number;
  gravity: string;
  alt: string;
};

export type CourseEditor = {
  id: number;
  name: string;
  description: string;
  courses: Course[];
};

export type Occupation = {
  id: number;
  title: string;
  description?: string;
  external_id?: string;
  group_title?: string;
  custom_category_id?: number;
  customCategoryTitle?: string;
  promoted?: boolean;
  coursesCount?: number;
  jobsCount?: number;
  tags?: string[];
};

type Category = {
  slug: string;
  title: string;
};

export type OccupationSubject = Readonly<{
  id: number;
  title: string;
  description: string;
  slug: string;
  category: Category;
  occupations: Occupation[];
  activitiesCount?: number;
  skillsCount?: number;
  sampleSkills?: string[];
}>;

export type User = Readonly<{
  id: number;
  email: string;
  profile: UserProfileExtended;
  email_confirmed: boolean;
  unconfirmed_email?: string;
  phone: string;
  provider: string | null;
  guardian: Guardian | null;
  disabled_fields: string[];
  providers_label?: string;
  locale: string;
  account_status: string;
  password?: string;
  password_confirmation?: string;
  password_warning?: boolean;
  password_warning_dismissed?: boolean;
  account_age_days: number;
  is_new_user: boolean;
  email_withheld?: boolean;
  missing_email_reason?: string | null;
  impersonator?: { id: number; email: string };
  editable_credentials?: Array<'email' | 'phone' | 'password'>;
  password_confirmation_required?: boolean;
  translator_token?: string;
  pusher_channel_name?: string;
  pusher?: {
    appKey: string;
    cluster?: string;
    channelName: string;
    authEndpoint: string;
  };
  /** @description if user has session scheduling credentials */
  can_schedule_sessions: boolean;
}>;

export type UserLanguage = {
  id: number;
  language: string;
  proficiency: number;
  includeOnCv: boolean;
  includeOnMentorship: boolean;
  assessmentType: 'YES' | 'NO' | 'UNKNOWN' | null;
  assessmentLevelType: 'CEFR' | 'OTHER' | 'UNKNOWN' | null;
  assessmentLevel: string | null;
};

type UserDocumentRequestType =
  | 'cv'
  | 'skill_match'
  | 'occupation_overview'
  | 'category_overview'
  | 'saved_overview';

export type CvLayout = 'modern' | 'simple' | 'classic';

export type CvFormat = 'pdf' | 'docx';

export type CVColorsPalette = 'primary' | 'job' | 'education' | 'hobby' | 'neutrals';
export type CVAccentColor = { paletteName: CVColorsPalette; value: string };
type PersonalStatement = null | { type: 'default' } | { type: 'custom'; value: string };

export type UserDocumentOptions = {
  occupationId?: number;
  categoryId?: number;
  saved?: boolean;
  layout?: CvLayout;
  format?: CvFormat;
  statement?: PersonalStatement;
  socialLinks?: SocialLink[];
  includeSocialLinks?: boolean;
  accentColor?: CVAccentColor;
};
export type UserDocumentRequest = {
  id: number;
  status: 'completed' | 'error' | 'requested';
  fileUrl?: string;
  shareableUrl: string;
  createdAt: string;
  emailRequested: boolean;
  shared: boolean;
  options?: UserDocumentOptions;
  type: UserDocumentRequestType;
  locale: string;
  notified?: boolean;
};
export type UserDocumentRequestInput = {
  documentType: UserDocumentRequestType;
  locale: string;
  options?: UserDocumentOptions;
  emailRequested?: boolean;
  shared?: boolean;
};

export type RecommendedSkill = Readonly<{
  id: number;
  skill_id: number;
  experience_id: number;
  external_id: string;
  skill_type: string;
  title: string;
  description?: string | null;
  priority?: number;
  timestamp: number;
}>;

type SkillType = 'skill' | 'knowledge';
export type SkillChoice = (typeof SKILL_CHOICES)[number];
export type ExperienceSkill = {
  id: number;
  choice: null | SkillChoice;
  description: null | string;
  experience_id: number;
  skill_id: number;
  external_id: string;
  skill_type: SkillType;
  is_mentorship_skill?: boolean;
  timestamp: number;
  title: string;
  source?: 'recommended' | 'manual';
  skill_category?: string;
  showOnCv: boolean;
};

export type CreateExperienceSkillParams = Pick<
  ExperienceSkill,
  'experience_id' | 'skill_id' | 'choice'
> &
  Required<Pick<ExperienceSkill, 'source'>>;

export type UpdateExperienceSkillParams = Pick<ExperienceSkill, 'id' | 'choice' | 'experience_id'>;

type SearchLocation = {
  country: string;
  // the bounds of the selected place, as returned by the Google Maps API
  viewport?: Bounds;
  // the bound of the viewport, extended by the proximity
  bounds?: Bounds;
  location: string;
  proximity: number;
};

export type ThemeColorsTypes =
  | 'primary'
  | 'error'
  | 'success'
  | 'neutrals'
  | 'job'
  | 'education'
  | 'hobby';
export type ThemeColors = Partial<Record<ThemeColorsTypes, string>>;

export type Motivation = 'skills' | 'job' | 'careers' | 'cv';

export type ProfileGenderValue = (typeof GENDER_OPTIONS)[number];
type OnboardingFields = {
  name: string;
  mandatory: boolean;
};

export type Guardian = {
  id: number;
  avatarUrl: Record<string, string> | null;
  firstName: string;
  lastName: string;
  fullNameLatin: string;
  status: 'pending' | 'accepted' | 'initial';
  profileId: number;
  tour: 'finished_tour' | 'unfinished_tour' | 'skipped_tour';
  maxDependents: number;
  supervisionLimitExceeded: boolean;
};

export type Profile = {
  assignment_id: number | null;
  access_criteria?: Record<string, CodeTitle[]>;
  id: number;
  email: string;
  first_name: string;
  disabled_fields: string[];
  last_name: string;
  full_name_latin: string;
  telephone: string;
  birthday: string;
  nationality: string;
  country: string;
  arrival_in_country: string;
  street_and_house_number: string;
  city: string;
  is_mentor: boolean;
  postcode: string;
  project_missing: boolean;
  project_id: number;
  project_external_id: string;
  project_title: string;
  project_contact_url?: string;
  platform_name: string;
  project_slug: string;
  project_logo: string;
  project_theme_colors: ThemeColors | null;
  projectContactEmailAddress?: string;
  enable_data_share: boolean;
  gender: ProfileGenderValue | null;
  gender_custom: string | null;
  genderChoices?: string[];
  read_privacy_policy_at: string | null;
  showPrivacyPolicyUpdates: boolean;
  statement: string | null;
  include_statement: boolean;
  include_avatar: boolean;
  tours: Record<string, string>;
  accessible_features: string[];
  last_active_project: string | null;
  can_submit_rating: boolean;
  // depends on fields enabled in onboarding feature
  national_id?: string;
  disabilities?: string[];
  disabilities_choice?: string;
  low_chances?: string[];
  low_chances_choice?: string;
  fathers_name?: string;
  grandfathers_name?: string;
  onboardingFields?: OnboardingFields[]; // readonly

  // only used for updating
  avatar?: string | null;
  avatar_urls: Record<string, string> | null;
  // TODO: remove this
  motivations: Motivation[] | null;
  goal: string | null;
  beneficiary_role: string | null;
  search_location?: SearchLocation | null;
  tips?: unknown;
  cube8_code?: string;
  profile_matches?: number;
  profile_matches_occupation_ids?: number[];
  allowed_languages?: string[];
  allowed_locales?: string[];
  social_links?: SocialLink[];
  include_social_links: boolean;
  has_work_permit?: boolean;
  exclude_birthday: boolean;
  is_lite: boolean | null;
  mentor_view: boolean;
  is_approved_mentor?: boolean;
  lite_state: string[] | null;
  onboarding_completed?: boolean;
};

export type SocialLink = {
  url: string;
  displayName?: string;
};

export type AppFeedback = {
  nps?: number;
  nps_description: string;
  satisfaction?: number;
  satisfaction_description: string;
  general_feedback: string;
  submitted?: boolean;
};

export type NotificationMessage = {
  id: number;
  type: 'error' | 'success' | 'warning' | 'info';
  text?: string;
  textKey?: string;
};

export type Job = {
  id: number;
  title: string;
  employer: string;
  postDate: string;
  description: string;
  description_original: string | null;
  url: string;
  salary: string;
  contractTime: string;
  contractType: string;
  category: string;
  locationLat: number | null;
  locationLong: number | null;
  locationCountry: string;
  locationDescription: string | null;
  locationParentDescription: string;
  matches?: Array<{ id: number; externalId: string; title?: string }>;
  occupationId?: number;
  phone?: string | null;
  vacancyId?: number;
  vacancyType?: OnJobTrainingVacancyTypes;
};

export type WithCancelToken<T = {}> = T & {
  cancelToken?: CancelToken;
};

export type SkillGroup = Readonly<{
  title: string;
  key: SkillGroups;
  code: string;
}>;

export type TestStepsType = (typeof CAREER_INTEREST_TESTS_STEPS)[number];

export type CareerInterestTestType = (typeof CAREER_INTEREST_TESTS)[number];

export type CareerInterestsTestData = {
  version: number;
  steps: Record<string, number | null>;
  rank: string[];
  currentStep: number;
  complete: boolean;
};

export type CareerInterestsTestRemoteData = Partial<
  Record<
    CareerInterestTestType,
    {
      id: number;
      data: CareerInterestsTestData;
    }
  >
>;

export type CvConfig = {
  id: number;
  name: CvConfigNames;
  value?: boolean;
};

export type CvConfigNames =
  | 'includeArrivalInCurrentCountry'
  | 'includeNationality'
  | 'includeExperienceSkills'
  | 'includeLanguages'
  | 'includeRoleDescription'
  | 'includePersonalStatement'
  | 'includePhoto'
  | 'showExperienceIcons'
  | 'includeSkillSection'
  | 'includeColorOptions'
  | 'layoutOptions'
  | 'layoutOptionsModern'
  | 'layoutOptionsClassic'
  | 'layoutOptionsSimple'
  | 'includeWorkPermit'
  | 'includeSocialLinks';
